import React, { useState } from "react";
import RegularImage from "../../../images/slides/slide-b.jpg";
import Content from "../../../layout/content/Content";
import Head from "../../../layout/head/Head";
import {
  Block,
  BlockDes,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  PreviewCard,
  BlockBetween,
  Button,
  Icon,
  TooltipComponent,
} from "../../../components/Component";
import { Label, Input, Row, Col, Alert } from "reactstrap";
import FormInput from "../../form-components/InputComponent";
import FormSelect from "../../form-components/SelectComponent";
import FormDatePicker from "../../form-components/DatePicker";
import { useFormContext } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import sites from "../../../api/master/sites";
import dayjs from "dayjs";
const SiteStatus: React.FC = (props: any) => {
  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
    setValue,
    trigger,
    reset,
  } = useFormContext();
  const siteDocuments = watch("siteDocuments");
  const hasSequence = watch("hasSequence");
  const referenceID = watch("referenceID");

  const [alertInfo, setAlertInfo] = useState({
    type: "", // 'success' or 'error'
    message: "",
    errors: [],
  });

  const CurrentStatusOptions = [
    { label: "Planned", value: "planned" },
    { label: "In Progress", value: "inprogress" },
    { label: "On Hold", value: "onhold" },
    { label: "Completed", value: "completed" },
  ];

  const submitForm = async (data: any) => {
    console.log("General Info Data:", props);
    console.log("Site setUP:", data);

    // If NO SEQUENCE → send referenceID as siteId
    if (!hasSequence && referenceID) {
      data.referenceID = referenceID;
    } else {
      delete data.referenceID;
    }

    // Check if this is the last step
    if (props.isLast) {
      // This is the last step, submit the form and call API
      try {
        const siteFormData: any = new FormData();
        if (data?.siteDocuments && data?.siteDocuments[0]) {
          siteFormData.append("files", data?.siteDocuments[0]);
          siteFormData.append(
            "filesData",
            JSON.stringify([
              {
                indexOfFileLocated: 0,
                module: "site",
                purpose: "Site Document",
                level: "",
                uniqueId: "",
                action: "Create",
              },
            ])
          );
        } else {
          siteFormData.append("files", JSON.stringify([]));
          siteFormData.append("filesData", JSON.stringify([]));
        }
        delete data.siteDocuments;
        delete data.siteAddress;

        // Format dates
        if (data.plannedStartDate) {
          data.plannedStartDate = dayjs(data.plannedStartDate).format(
            "YYYY-MM-DD"
          );
        }
        if (data.plannedEndDate) {
          data.plannedEndDate = dayjs(data.plannedEndDate).format("YYYY-MM-DD");
        }

        // Format Blocks dates if they exist
        if (data.Blocks && Array.isArray(data.Blocks)) {
          data.Blocks = data.Blocks.map((block: any) => ({
            ...block,
            startDate: block.startDate
              ? dayjs(block.startDate).format("YYYY-MM-DD")
              : null,
            endDate: block.endDate
              ? dayjs(block.endDate).format("YYYY-MM-DD")
              : null,
          }));
        }

        siteFormData.append("siteData", JSON.stringify(data));
        const response = await sites.createSite(siteFormData);
        const { status, message } = response;
        if (status) {
          Swal.fire("Created!", "Site has been created", "success");
          navigate(`/sites`);
        } else {
          let errorMessage = "An error occurred";
          const fullErrorMessage =
            message && Array.isArray(message)
              ? `${errorMessage}\n${message.join(", ")}`
              : message || errorMessage;
          setAlertInfo({
            type: "error",
            message: fullErrorMessage,
            errors: [],
          });
        }
      } catch (error: any) {
        console.error("Error creating site:", error);
        Swal.fire({
          icon: "error",
          title: "Error!",
          text:
            error?.response?.data?.message ||
            "Failed to create site. Please try again.",
        });
      }
    } else {
      // Not the last step, proceed to next
      if (props.next && typeof props.next === "function") {
        props.next();
      } else {
        console.error("props.next is not a function", props);
      }
    }
  };

  const handleFormError = (errors: any) => {
    console.log("Form validation errors:", errors);
    // Show validation errors to user
    const errorFields = Object.keys(errors);
    if (errorFields.length > 0) {
      const firstError = errors[errorFields[0]];
      const errorMessage =
        firstError?.message || "Please fill in all required fields";
      Swal.fire({
        icon: "warning",
        title: "Validation Error",
        text: errorMessage,
      });
    }
  };
  return (
    <>
      {alertInfo.message && (
        <div className="mb-3 mt-1">
          <Alert
            color={alertInfo.type === "error" ? "danger" : "success"}
            className="alert-icon"
          >
            <strong>{alertInfo.message}</strong>
          </Alert>
        </div>
      )}
      <form
        className="content clearfix"
        onSubmit={handleSubmit(submitForm, handleFormError)}
      >
        <Row>
          <Col lg="4">
            <FormDatePicker
              name="plannedStartDate"
              label="Planned Start Date"
              // minDate={new Date()} // Today's date as the minimum date
              // maxDate={new Date(new Date().setFullYear(new Date().getFullYear() + 1))} // Maximum date set to one year from today
              required={true}
              showYearDropdown={true}
              scrollableYearDropdown={true}
            />
          </Col>
          <Col lg="4">
            <FormDatePicker
              name="plannedEndDate"
              label="Planned End Date"
              // minDate={new Date()} // Today's date as the minimum date
              // maxDate={new Date(new Date().setFullYear(new Date().getFullYear() + 1))} // Maximum date set to one year from today
              required={true}
              customValidation={{
                validate: (selectedDate: any) => {
                  const startDate = watch("plannedStartDate");
                  if (!startDate) {
                    return true; // Skip validation if start date is not selected
                  }
                  if (!selectedDate) {
                    return "Planned End Date is required";
                  }
                  // Compare dates properly
                  const start = new Date(startDate);
                  const end = new Date(selectedDate);
                  return (
                    end > start || "End date must be later than start date"
                  );
                },
              }}
              showYearDropdown={true}
              scrollableYearDropdown={true}
            />
          </Col>
          <Col lg="4">
            <FormSelect
              name="currentStatus"
              label="Current Status"
              options={CurrentStatusOptions}
              required={true}
              placeholder="-Select-"
            />
          </Col>
        </Row>
        <Row className="mt-4">
          {/* <Col lg="4">
            <FormInput
              name="siteDocuments"
              label="Site Document (if any)"
              type="file"
              placeholder="Enter Site Document (if any)"
            />
            {siteDocuments && <div>{siteDocuments[0]?.name}</div>}
          </Col> */}
          <Col lg="4">
            {siteDocuments && siteDocuments[0]?.name && (
              <div className="form-group">
                <div className="form-label-wrapper">
                  <label className="form-label" htmlFor={"siteDocuments"}>
                    Enter Site Document (if any)
                  </label>
                  <TooltipComponent
                    iconClass="card-hint"
                    icon="help-fill"
                    direction="left"
                    id={`siteDocuments-tooltip`}
                    text="Only PNG, JPG and JPEG files are allowed"
                    containerClassName={""}
                  />
                </div>
                <div
                  className="form-control-wrap d-flex align-items-center justify-content-between border p-1"
                  style={{ borderRadius: "3px" }}
                >
                  <span>{siteDocuments[0]?.name}</span>
                  <span
                    onClick={() => {
                      setValue("siteDocuments", null);
                    }}
                    style={{
                      cursor: "pointer",
                      marginLeft: "10px",
                      display: "block",
                      fontSize: 14,
                    }}
                  >
                    <Icon name="cross"></Icon> &nbsp;
                  </span>
                </div>
              </div>
            )}
            <div
              style={{
                display: `${siteDocuments && siteDocuments[0]?.name ? "none" : "block"
                  }`,
              }}
            >
              <FormInput
                name="siteDocuments"
                label="Site Document"
                type="file"
                placeholder="site Document Upload"
              />
            </div>
          </Col>
        </Row>

        <div className="actions clearfix">
          <ul>
            <li>
              <Button color="primary" type="submit">
                {props.isLast ? "Create Site" : "Next"}
              </Button>
            </li>
            <li>
              <Button
                color="primary"
                onClick={props.prev}
                className="btn-dim btn btn-secondary"
              >
                Previous
              </Button>
            </li>
          </ul>
        </div>
      </form>
    </>
  );
};
export default SiteStatus;
